/**
 * How the day's calories were split between its meals.
 *
 * Derived entirely from rows already on screen — nothing here is fetched or inferred. The stacked
 * bar, its legend and the `X% מהיום` pill in each meal header all read this one function, so they
 * cannot end up disagreeing about what a quarter of the day looks like.
 */

import { v2 } from "../ui/v2Tokens";

/**
 * Just enough of a meal to place it in the bar.
 *
 * Deliberately structural rather than the food-log's own meal type: the caller already knows each
 * meal's calories — it computes them with the same `actualMealTotals` the meal header prints — and
 * re-deriving them here from raw rows is how the pill and the header end up disagreeing. It also
 * keeps this function free of how a day is resolved, which changed once already.
 */
export type MealCalories = {
  uid: string;
  name: string;
  calories: number;
};

export type MealShare = MealCalories & {
  /** whole percent; the shares of a day always sum to exactly 100 */
  pct: number;
  color: string;
};

/**
 * Largest remainder, so three equal meals read 34/33/33 rather than 33/33/33 — a legend whose
 * percentages visibly fail to add up reads as a bug in the numbers rather than in the rounding, and
 * the bar it labels would end with a sliver of empty track.
 */
export const mealShares = (meals: MealCalories[]): MealShare[] => {
  const calories = meals.map((meal) => meal.calories);
  const total = calories.reduce((a, b) => a + b, 0);
  // A day with no calories has no composition. Returning nothing is the honest answer, and it keeps
  // 0/0 out of the arithmetic below.
  if (total <= 0) return [];

  const exact = calories.map((c) => (c / total) * 100);
  const pcts = exact.map(Math.floor);
  let left = 100 - pcts.reduce((a, b) => a + b, 0);
  const byRemainder = exact
    .map((value, i) => ({ i, remainder: value - Math.floor(value) }))
    .sort((a, b) => b.remainder - a.remainder);
  for (const { i } of byRemainder) {
    if (left <= 0) break;
    pcts[i] += 1;
    left -= 1;
  }

  return meals
    .map((meal, i) => ({
      uid: meal.uid,
      name: meal.name,
      calories: calories[i],
      pct: pcts[i],
      color: v2.brandRamp[i % v2.brandRamp.length],
    }))
    // a meal that contributed nothing is not a zero-width segment, it is not a segment
    .filter((share) => share.calories > 0);
};
